import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { isFileDialogGuardActive } from '../../utils/fileDialogGuard';

export interface DrawerProps {
  /** Whether the drawer is open */
  isOpen: boolean;
  /** Callback when the drawer should close */
  onClose: () => void;
  /** Called before any user-initiated close (X button / Esc / backdrop). Return
   *  false to VETO the close — e.g. to show an unsaved-changes confirm modal
   *  first. When omitted, closes proceed unconditionally. */
  onBeforeClose?: () => boolean;
  /** Title displayed in the drawer header */
  title: string | React.ReactNode;
  /** Optional content rendered in the header's right side, before the close X
   *  (e.g. a "Revision History" link). */
  headerActions?: React.ReactNode;
  /** Content to display in the drawer */
  children: React.ReactNode;
  /** Confirmation modal rendered above the drawer panel in its dialog layer. */
  overlay?: React.ReactNode;
  /** Position of the drawer (default: 'right') */
  position?: 'left' | 'right';
  /** Maximum width of the drawer (default: 'md' = 28rem) */
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  /** Fixed width in pixels (overrides maxWidth if provided) */
  width?: string;
  /** Custom className for the drawer panel */
  className?: string;
  /** Optional resize grip, positioned inside the panel. */
  resizeHandle?: React.ReactNode;
  /** Footer content (buttons, actions, etc.) */
  footer?: React.ReactNode;
  /** Hide backdrop overlay and blur (default: false) */
  noBackdrop?: boolean;
  /** Disable close on click outside (default: false) */
  disableClickOutside?: boolean;
  /** Container element to render drawer within (default: document.body) - uses absolute positioning instead of portal */
  containerRef?: React.RefObject<HTMLElement | null>;
  /** Completely hide the drawer (display: none) - for screenshot capture */
  forceHidden?: boolean;
  /** Custom className for the content area */
  contentClassName?: string;
  /** Override the header's padding classes (default 'px-4 py-3 sm:px-6'); the
   *  header border + background are always applied. Lets a caller make its
   *  drawer header tighter without affecting other drawers. */
  headerClassName?: string;
}

const maxWidthClasses = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  full: 'max-w-full',
};

export const Drawer: React.FC<DrawerProps> = ({
  isOpen,
  onClose,
  onBeforeClose,
  title,
  headerActions,
  children,
  overlay,
  position = 'right',
  maxWidth = 'md',
  width,
  className = '',
  resizeHandle,
  footer,
  noBackdrop = false,
  disableClickOutside = false,
  containerRef,
  forceHidden = false,
  contentClassName,
  headerClassName,
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // For noBackdrop mode, we use a div instead of dialog, so handle animation state directly
    if (noBackdrop) {
      if (isOpen && !forceHidden) {
        setIsAnimating(true);
      } else {
        setIsAnimating(false);
      }
      return;
    }

    const dialog = dialogRef.current;
    if (!dialog) return;

    if (isOpen && !forceHidden && !dialog.open) {
      dialog.showModal();
      setIsAnimating(true);
    } else if (forceHidden && dialog.open) {
      // Close dialog when force hidden to remove backdrop
      dialog.close();
      setIsAnimating(false);
    } else if (!isOpen && dialog.open) {
      // Close with animation
      setIsAnimating(false);
      const timer = setTimeout(() => {
        dialog.close();
      }, 500); // Match transition duration
      return () => clearTimeout(timer);
    }
  }, [isOpen, forceHidden, noBackdrop]);

  // Handle closing animation
  const handleClose = () => {
    // Let the caller veto the close (e.g. unsaved changes → show a modal first).
    if (onBeforeClose && onBeforeClose() === false) return;
    setIsClosing(true);
    setIsAnimating(false);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 500); // Match transition duration
  };

  // Track whether mousedown started outside the panel
  const mouseDownOutsideRef = useRef(false);

  const handleBackdropMouseDown = (e: React.MouseEvent) => {
    // Check if click is outside the panel AND not on a portaled element inside the dialog
    // (e.g. TypeAhead dropdowns portal into the dialog but outside panelRef)
    const isOutsidePanel = !panelRef.current?.contains(e.target as Node);
    const isOnBackdrop = e.target === e.currentTarget;
    mouseDownOutsideRef.current = isOutsidePanel && isOnBackdrop;
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (isFileDialogGuardActive()) return; // a file picker opened from inside is being dismissed
    const isOutsidePanel = !panelRef.current?.contains(e.target as Node);
    const isOnBackdrop = e.target === e.currentTarget;
    if (!disableClickOutside && mouseDownOutsideRef.current && isOutsidePanel && isOnBackdrop) {
      handleClose();
    }
  };

  // Handle escape key
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    const handleCancel = (e: Event) => {
      e.preventDefault();
      if (isFileDialogGuardActive()) return; // file picker dismissal leaking through
      if (!disableClickOutside) {
        handleClose();
      }
    };

    dialog.addEventListener('cancel', handleCancel);
    return () => dialog.removeEventListener('cancel', handleCancel);
  }, [onClose, onBeforeClose, disableClickOutside]);

  const translateClass = position === 'right'
    ? (isAnimating ? 'translate-x-0' : 'translate-x-full')
    : (isAnimating ? 'translate-x-0' : '-translate-x-full');

  const positionClass = position === 'right' ? 'ml-auto' : 'mr-auto';
  const paddingClass = position === 'right' ? 'pl-0 sm:pl-10 md:pl-16' : 'pr-0 sm:pr-10 md:pr-16';
  // On mobile, force panel to full width regardless of width prop
  const mobileFullWidth = width ? 'max-sm:!w-full' : '';

  const backdropClass = noBackdrop
    ? "backdrop:bg-transparent backdrop:pointer-events-none"
    : "backdrop:bg-gray-900/50 backdrop:backdrop-blur-sm";

  // Trigger animation when opening for container-relative drawer
  useEffect(() => {
    if (containerRef?.current && isOpen) {
      // Small delay to ensure element is mounted before animating
      const timer = setTimeout(() => setIsAnimating(true), 10);
      return () => clearTimeout(timer);
    } else if (containerRef?.current && !isOpen) {
      setIsAnimating(false);
    }
  }, [isOpen, containerRef]);

  // Drawer content
  const drawerContent = (
    <div className="relative flex h-full flex-col overflow-y-auto bg-cmp-drawer shadow-xl">
      {/* Header */}
      <div className={`${headerClassName ?? 'px-4 py-3 sm:px-6'} shrink-0 border-b border-theme-border-primary bg-cmp-drawer-header`}>
        <div className="flex items-start justify-between">
          <h2
            id="drawer-title"
            className="text-base font-semibold text-cmp-drawer-header-fg"
            style={{ paddingTop: '2px' }}
          >
            {title}
          </h2>
          <div className="ml-3 flex h-7 items-center gap-4">
            {headerActions}
            <button
              type="button"
              onClick={handleClose}
              className="relative rounded-md text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 transition-colors"
            >
              <span className="absolute -inset-2.5"></span>
              <span className="sr-only">Close panel</span>
              <XMarkIcon className="size-6" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className={contentClassName || "relative flex-1 px-4 py-6 sm:px-6 overflow-y-auto"}>
        {children}
      </div>

      {/* Footer */}
      {footer && (
        <div className="shrink-0 px-4 py-4 sm:px-6 border-t border-theme-border-primary bg-cmp-drawer">
          {footer}
        </div>
      )}
      {resizeHandle}
    </div>
  );

  // Container-relative drawer (absolute positioning within container)
  if (containerRef?.current) {
    if (!isOpen && !isAnimating) return null;

    return createPortal(
      <div
        ref={panelRef as React.RefObject<HTMLDivElement>}
        className={`absolute top-0 ${position === 'left' ? 'left-0' : 'right-0'} h-full ${width ? '' : maxWidthClasses[maxWidth]} transform transition-transform duration-500 ease-in-out ${translateClass} ${className} z-[60]`}
        style={{ maxHeight: '100%', ...(width ? { width } : {}) }}
      >
        {drawerContent}
      </div>,
      containerRef.current
    );
  }

  // Portal-based drawer (full screen with dialog)
  // Use higher z-index when noBackdrop to ensure it appears above sidebar navigation
  const zIndexClass = noBackdrop ? 'z-[100]' : 'z-[60]';

  // When noBackdrop is true, use a div instead of dialog to allow clicking through
  if (noBackdrop) {
    if (!isOpen && !isAnimating) return null;

    return createPortal(
      <div
        className={`fixed inset-0 ${zIndexClass}`}
        style={{ pointerEvents: 'none' }}
      >
        <div
          className={`absolute inset-0 ${paddingClass}`}
          style={{ pointerEvents: 'none' }}
        >
          <div
            ref={panelRef}
            className={`${positionClass} block size-full ${width ? '' : maxWidthClasses[maxWidth]} ${mobileFullWidth} transform transition-transform duration-500 ease-in-out ${translateClass} ${className}`}
            style={{ ...(width ? { width } : {}), pointerEvents: 'auto' }}
          >
            {drawerContent}
          </div>
        </div>
      </div>,
      document.body
    );
  }

  // Don't render dialog when closed and not animating
  if (!isOpen && !isAnimating) return null;

  return createPortal(
    <dialog
      ref={dialogRef}
      aria-labelledby="drawer-title"
      className={`fixed inset-0 size-auto max-h-none max-w-none overflow-hidden bg-transparent ${zIndexClass} ${backdropClass}`}
      style={forceHidden ? { display: 'none', pointerEvents: 'none' } : undefined}
    >
      <div
        className={`absolute inset-0 ${paddingClass}`}
        onMouseDown={handleBackdropMouseDown}
        onClick={handleBackdropClick}
      >
        <div
          ref={panelRef}
          className={`${positionClass} block size-full ${width ? '' : maxWidthClasses[maxWidth]} ${mobileFullWidth} transform transition-transform duration-500 ease-in-out ${translateClass} ${className}`}
          style={width ? { width } : {}}
        >
          {drawerContent}
        </div>
      </div>
      {overlay}
    </dialog>,
    document.body
  );
};

export default Drawer;
